import { Modal, RadioInput, Typography } from 'melp-design/components';
import { Stack } from '@mui/material';
import {
  CakeOutlined,
  EmailOutlined,
  PhoneOutlined,
} from '@mui/icons-material';
import { FC, ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';

interface Consents {
  shareEmail: boolean;
  sharePhoneNumber: boolean;
  shareBirthdate: boolean;
}

type TriState = 'noChange' | 'yes' | 'no';

type FormValues = Record<keyof Consents, TriState>;

const INITIAL_VALUES: FormValues = {
  shareBirthdate: 'noChange',
  sharePhoneNumber: 'noChange',
  shareEmail: 'noChange',
};

const toPartialConsents = (values: FormValues): Partial<Consents> => {
  const consents: Partial<Consents> = {};
  (Object.keys(values) as Array<keyof FormValues>).forEach((key) => {
    const value = values[key];
    if (value !== 'noChange') {
      consents[key] = value === 'yes';
    }
  });
  return consents;
};

interface Props extends ModalProps {
  closeModal: (
    param?:
      | { action: 'CLOSE' }
      | { action: 'CONFIRM'; consents: Partial<Consents> },
  ) => void;
}

export const UpdateConsentsModal: FC<Props> = ({ closeModal }) => {
  const { t } = useTranslation();

  const { control, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: INITIAL_VALUES,
  });

  const values = watch();
  const hasChanges = Object.keys(toPartialConsents(values)).length > 0;

  const onSubmit: SubmitHandler<FormValues> = (formValues) => {
    closeModal({
      action: 'CONFIRM',
      consents: toPartialConsents(formValues),
    });
  };

  const rows: Array<{
    name: keyof FormValues;
    icon: ComponentType<{ fontSize?: 'small' }>;
    label: string;
  }> = [
    {
      name: 'shareBirthdate',
      icon: CakeOutlined,
      label: t('employees.consents.shareBirthdate.label'),
    },
    {
      name: 'sharePhoneNumber',
      icon: PhoneOutlined,
      label: t('employees.consents.sharePhoneNumber.label'),
    },
    {
      name: 'shareEmail',
      icon: EmailOutlined,
      label: t('employees.consents.shareEmail.label'),
    },
  ];

  const radioItems: Array<{ label: string; value: TriState }> = [
    { label: t('employees.consents.noChange.label'), value: 'noChange' },
    { label: t('common.yes'), value: 'yes' },
    { label: t('common.no'), value: 'no' },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('employees.consents.actions.update')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.save'),
            type: 'submit',
            disabled: !hasChanges,
          },
        ]}
      >
        <Stack gap={2}>
          {rows.map(({ name, icon: Icon, label }) => (
            <Stack
              key={name}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <Icon fontSize="small" />
                <Typography variant="p1">{label}</Typography>
              </Stack>
              <Controller
                name={name}
                control={control}
                render={({ field: { value, onChange, ref } }) => (
                  <RadioInput
                    items={radioItems}
                    direction="row"
                    name={name}
                    value={value}
                    onChange={onChange}
                    ref={ref}
                  />
                )}
              />
            </Stack>
          ))}
        </Stack>
      </Modal.Content>
    </form>
  );
};
